<template>
  <div class="p-4 sm:p-6 md:p-8">
    <div class="max-w-7xl mx-auto">
      <div class="flex items-center justify-between mb-6">
        <h1 class="text-2xl sm:text-3xl font-bold text-gray-800">Report an Issue</h1>
        <router-link :to="{ name: 'ClientAdminDashboard' }" class="text-sm text-brand-primary hover:text-brand-accent hover:underline">
          &larr; Back to Dashboard
        </router-link>
      </div>

      <p class="text-sm text-gray-600 mb-6 bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-r-lg">
        Please check the list of outstanding issues below before submitting a new report to avoid duplicates.
      </p>

      <div class="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
        
        <!-- Left Column: Existing Reports -->
        <ClientIssueReportsList />

        <!-- Right Column: Form -->
        <div class="bg-white p-6 sm:p-8 rounded-lg shadow-xl">
          <div v-if="loading" class="text-center py-10">
              <p class="text-gray-600">Loading your details...</p>
          </div>
          <form v-else @submit.prevent="handleSubmit" class="space-y-6">
            <section>
              <h2 class="text-xl font-semibold text-gray-700 mb-4">Submit New Issue</h2>
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-4">
                <div>
                  <label class="form-label-readonly">Full Name</label>
                  <p class="form-text-readonly">{{ formData.reporterName }}</p>
                </div>
                <div>
                  <label class="form-label-readonly">Email Address</label>
                  <p class="form-text-readonly">{{ formData.reporterEmail }}</p>
                </div>
              </div>
               <div>
                <label for="comments" class="form-label">
                  Please describe the issue in detail <span class="text-red-500">*</span>
                </label>
                <textarea 
                  id="comments" 
                  v-model="formData.comments" 
                  rows="6" 
                  required 
                  class="form-input mt-1"
                  placeholder="Describe the problem, including steps to reproduce it."
                ></textarea>
              </div>
            </section>

            <section>
              <h3 class="text-lg font-medium text-gray-800">Attach Screenshot (Optional)</h3>
              <ImageUploader 
                id="issue-screenshot" 
                label="Upload a screenshot of the issue" 
                @update:files="updateImageFile" 
                :multiple="false"
              />
            </section>

            <div class="pt-4 border-t border-gray-200">
                <div class="flex justify-end">
                  <button type="submit" :disabled="isSubmitting" class="form-action-btn-primary">
                    <span v-if="isSubmitting">Submitting Report...</span>
                    <span v-else>Submit Report</span>
                  </button>
                </div>
              </div>

              <p v-if="submissionError" class="mt-2 text-sm text-red-600 text-center">{{ submissionError }}</p>
              <p v-if="successMessage" class="mt-2 text-sm text-green-600 text-center">{{ successMessage }}</p>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue';
import { db, auth, firebaseApp } from '@/firebaseConfig';
import { doc, getDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import ImageUploader from '@/components/ImageUploader.vue';
import ClientIssueReportsList from '@/components/client-admin/ClientIssueReportsList.vue';

const functions = getFunctions(firebaseApp, 'europe-west2');

const loading = ref(true);
const isSubmitting = ref(false);
const submissionError = ref('');
const successMessage = ref('');
const imageFile = ref(null);

const formData = reactive({
  reporterName: '',
  reporterEmail: '',
  comments: '',
});

const updateImageFile = (files) => {
  imageFile.value = files.length > 0 ? files[0] : null;
};

const fileToBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
        const result = reader.result;
        if (typeof result === 'string') {
            resolve(result.split(',')[1]); 
        } else {
            reject(new Error("FileReader did not return a string."));
        }
    };
    reader.onerror = error => reject(error);
});

const loadPrerequisites = async () => {
  loading.value = true;
  try {
    const user = auth.currentUser;
    if (!user) throw new Error("Authentication error.");

    const userProfileRef = doc(db, 'userProfiles', user.uid);
    const userProfileSnap = await getDoc(userProfileRef);

    if (userProfileSnap.exists()) {
      const profileData = userProfileSnap.data();
      formData.reporterName = `${profileData.firstName || ''} ${profileData.lastName || ''}`.trim();
      formData.reporterEmail = profileData.email || '';
    } else {
      throw new Error("Could not find your user profile.");
    }
  } catch (error) {
    submissionError.value = `Error loading your details: ${error.message}`;
  } finally {
    loading.value = false;
  }
};

onMounted(() => {
  loadPrerequisites();
});

const handleSubmit = async () => {
  if (!formData.comments) {
    submissionError.value = 'Please describe the issue before submitting.';
    return;
  }
  isSubmitting.value = true;
  submissionError.value = '';
  successMessage.value = '';

  try {
    let imageBase64 = null;
    if (imageFile.value) {
        imageBase64 = await fileToBase64(imageFile.value);
    }

    const submitReportFunction = httpsCallable(functions, 'submitIssueReport');
    
    const result = await submitReportFunction({
      comments: formData.comments,
      imageBase64: imageBase64,
    });

    if (result.data.success) {
      successMessage.value = 'Your report has been submitted successfully. A Sentinel Safe representative will be notified.';
      formData.comments = '';
      imageFile.value = null; 
      // Manually clear the uploader component if possible, though it's not critical
    } else {
      throw new Error(result.data.error || 'An unknown error occurred on the server.');
    }
  } catch (error) {
    console.error("Error submitting issue report:", error);
    submissionError.value = `Failed to submit report. Please try again. Error: ${error.message}`;
  } finally {
    isSubmitting.value = false;
  }
};
</script>

<style scoped>
.form-label { @apply block text-sm font-medium text-gray-700; }
.form-input { @apply mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-brand-primary focus:border-brand-primary sm:text-sm; }
.form-label-readonly { @apply block text-xs font-medium text-gray-500; }
.form-text-readonly { @apply mt-1 text-sm text-gray-800 font-medium bg-gray-100 px-3 py-2 rounded-md; }
.form-action-btn-primary { @apply bg-brand-primary text-white py-2 px-4 rounded-md hover:bg-brand-accent transition-colors duration-200 text-sm font-medium shadow-sm disabled:opacity-50; }
</style>